import React, { useEffect, useRef, useState } from "react";

/**
 * Interactive neon particle mesh — dots drift, connect with lines when close,
 * and lean away from the mouse cursor. Sits absolute inside a parent.
 */
export function HeroParticles({ colors = ["#FF3366", "#00E5FF", "#CCFF00"] }) {
  const cnv = useRef(null);
  const raf = useRef(0);
  const dots = useRef([]);
  const mouse = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const c = cnv.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      c.width = c.offsetWidth * dpr;
      c.height = c.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const density = window.innerWidth < 800 ? 40 : 70;
    dots.current = new Array(density).fill(0).map(() => ({
      x: Math.random() * c.offsetWidth,
      y: Math.random() * c.offsetHeight,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      color: colors[Math.floor(Math.random() * colors.length)],
      r: 1.2 + Math.random() * 1.8,
    }));

    const step = () => {
      raf.current = requestAnimationFrame(step);
      ctx.clearRect(0, 0, c.offsetWidth, c.offsetHeight);
      const arr = dots.current;
      // Move + repel from mouse
      for (const d of arr) {
        const mx = mouse.current.x - d.x;
        const my = mouse.current.y - d.y;
        const md = Math.hypot(mx, my);
        if (md < 120 && md > 0) {
          d.vx -= (mx / md) * 0.06;
          d.vy -= (my / md) * 0.06;
        }
        d.x += d.vx; d.y += d.vy;
        d.vx *= 0.985; d.vy *= 0.985;
        if (d.x < 0 || d.x > c.offsetWidth) d.vx *= -1;
        if (d.y < 0 || d.y > c.offsetHeight) d.vy *= -1;
      }
      // Lines
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const a = arr[i], b = arr[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < 140) {
            ctx.strokeStyle = a.color;
            ctx.globalAlpha = (1 - d / 140) * 0.25;
            ctx.lineWidth = 0.8;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      // Dots
      for (const d of arr) {
        ctx.shadowBlur = 10; ctx.shadowColor = d.color;
        ctx.fillStyle = d.color;
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.shadowBlur = 0;
    };
    step();

    const onMove = (e) => {
      const r = c.getBoundingClientRect();
      mouse.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { mouse.current = { x: -9999, y: -9999 }; };
    c.addEventListener("mousemove", onMove);
    c.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", resize);
      c.removeEventListener("mousemove", onMove);
      c.removeEventListener("mouseleave", onLeave);
    };
  }, [colors]);

  return (
    <canvas
      ref={cnv}
      className="absolute inset-0 pointer-events-auto"
      style={{ width: "100%", height: "100%", opacity: 0.7 }}
      data-testid="hero-particles"
    />
  );
}

/**
 * Rotating typewriter tagline. Types words[i], holds, deletes, moves to next.
 */
export function Typewriter({ words, className = "", color = "var(--sc-volt)" }) {
  const [i, setI] = useState(0);
  const [text, setText] = useState("");
  const [dir, setDir] = useState(1); // 1 typing, -1 deleting

  useEffect(() => {
    const current = words[i];
    if (dir === 1 && text.length === current.length) {
      const t = setTimeout(() => setDir(-1), 1600);
      return () => clearTimeout(t);
    }
    if (dir === -1 && text.length === 0) {
      setDir(1);
      setI((i + 1) % words.length);
      return;
    }
    const delay = dir === 1 ? 60 + Math.random() * 40 : 30;
    const t = setTimeout(() => {
      setText(current.slice(0, text.length + dir));
    }, delay);
    return () => clearTimeout(t);
  }, [text, dir, i, words]);

  return (
    <span className={className} style={{ color }} data-testid="hero-typewriter">
      {text}
      <span className="typewriter-caret" style={{ color }}>▍</span>
    </span>
  );
}

/**
 * Mouse-parallax hook. Returns a style object with CSS custom properties `--mx`/`--my`
 * ranging from -1..1 based on cursor distance from viewport center.
 */
export function useMouseParallax() {
  const [xy, setXy] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const on = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      setXy({ x: (e.clientX / w - 0.5) * 2, y: (e.clientY / h - 0.5) * 2 });
    };
    window.addEventListener("mousemove", on);
    return () => window.removeEventListener("mousemove", on);
  }, []);
  return xy;
}
